/**
 * Airliner-specific API service functions
 */
import apiClient from './apiClient';

export const airlinerService = {
  // Aircraft Management
  getAircraft: async () => {
    return apiClient.get('/aircraft');
  },

  getAircraftById: async (aircraftId) => {
    return apiClient.get(`/aircraft/${aircraftId}`);
  },

  getAircraftRoutes: async (aircraftId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    
    const queryString = queryParams.toString();
    return apiClient.get(`/aircraft/${aircraftId}/routes${queryString ? `?${queryString}` : ''}`);
  },

  addAircraft: async (aircraftData) => {
    return apiClient.post('/aircraft', aircraftData);
  },

  updateAircraft: async (aircraftId, aircraftData) => {
    return apiClient.put(`/aircraft/${aircraftId}`, aircraftData);
  },

  deleteAircraft: async (aircraftId) => {
    return apiClient.delete(`/aircraft/${aircraftId}`);
  },

  uploadAircraftImage: async (aircraftId, imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return apiClient.upload(`/aircraft/${aircraftId}/image`, formData);
  },

  // Flight Management
  getFlights: async () => {
    return apiClient.get('/airliner/flights');
  },

  addFlight: async (flightData) => {
    return apiClient.post('/airliner/flights', flightData);
  },

  updateFlight: async (flightId, flightData) => {
    return apiClient.put(`/airliner/flights/${flightId}`, flightData);
  },

  deleteFlight: async (flightId) => {
    return apiClient.delete(`/airliner/flights/${flightId}`);
  },

  getFlightBookings: async (flightId) => {
    return apiClient.get(`/airliner/flights/${flightId}/bookings`);
  },

  // Route Management
  getRoutes: async () => {
    return apiClient.get('/airliner/routes');
  },

  addRoute: async (routeData) => {
    return apiClient.post('/airliner/routes', routeData);
  },

  updateRoute: async (routeId, routeData) => {
    return apiClient.put(`/airliner/routes/${routeId}`, routeData);
  },

  deleteRoute: async (routeId) => {
    return apiClient.delete(`/airliner/routes/${routeId}`);
  },

  // Airport Management
  getAirports: async () => {
    return apiClient.get('/airliner/airports');
  },

  addAirport: async (airportData) => {
    return apiClient.post('/airliner/airports', airportData);
  },

  updateAirport: async (airportId, airportData) => {
    return apiClient.put(`/airliner/airports/${airportId}`, airportData);
  },

  deleteAirport: async (airportId) => {
    return apiClient.delete(`/airliner/airports/${airportId}`);
  },

  // Analytics
  getAnalytics: async (dateRange) => {
    return apiClient.get('/airliner/analytics', dateRange);
  },

  getBookingAnalytics: async (dateRange) => {
    return apiClient.get('/airliner/analytics/bookings', dateRange);
  },

  getRevenueAnalytics: async (dateRange) => {
    return apiClient.get('/airliner/analytics/revenue', dateRange);
  },

  // Reports
  generateReport: async (reportType, params) => {
    return apiClient.post('/airliner/reports/generate', { type: reportType, ...params });
  },

  downloadReport: async (reportId) => {
    return apiClient.get(`/airliner/reports/${reportId}/download`, {}, {
      responseType: 'blob'
    });
  },

  // Profile Management
  getProfile: async () => {
    return apiClient.get('/airliner/profile');
  },

  updateProfile: async (profileData) => {
    return apiClient.put('/airliner/profile', profileData);
  },

  changePassword: async (passwordData) => {
    return apiClient.post('/airliner/profile/change-password', passwordData);
  },

  uploadLogo: async (logoFile) => {
    const formData = new FormData();
    formData.append('logo', logoFile);
    return apiClient.upload('/airliner/profile/logo', formData);
  },

  // Notifications
  getNotifications: async () => {
    return apiClient.get('/airliner/notifications');
  },

  markNotificationRead: async (notificationId) => {
    return apiClient.put(`/airliner/notifications/${notificationId}/read`);
  },

  // Settings
  getSettings: async () => {
    return apiClient.get('/airliner/settings');
  },

  updateSettings: async (settings) => {
    return apiClient.put('/airliner/settings', settings);
  }
};

export default airlinerService;